import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {useQuery} from "react-query";
import {useTranslation} from "react-i18next";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import {format as formatDateFns} from "date-fns";
import {de as deLocale} from "date-fns/locale";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {Page} from "crqlar-react-common/dist/models/common/page.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {useServices} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useResources} from "crqlar-react-common/dist/utils/hooks/resources/useResources";
import {useAllResourceCategories} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import {useAllSectors, useSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useProfiles} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {getProfileFullName} from "crqlar-react-common/dist/utils/profile.util";
import {GeneralSet} from "crqlar-react-common/dist/utils/generalSet";
import {getLocalizedValue, getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {addDays, endOfDay, endOfMonth, formatDateHM, formatFullDate, startOfDay,} from "crqlar-react-common/dist/utils/date.util";
import {usePrint} from "crqlar-react-common/dist/utils/hooks/print/usePrint";
import {
    AgendaDay,
    AgendaItem,
    AgendaLayout,
    AgendaScope,
    buildAgendaItems,
    collectGroupIds,
    collectProfileIds,
    filterAccommodationReservations,
    getBookedForName,
    getFilteredResourceNames,
    getGuestNote,
    getItemGuestNames,
    getItemTimeRange,
    groupAgendaItemsByDay,
    groupAgendaItemsByGuest,
    ResourceFilter,
} from "./agenda/agenda.util";
import {collectStaffCategoryIds} from "../../common/uiElements/utils";

interface Props {
    profileId: string;
}

// v2 used [COMPLETED, CONFIRMED, PENDING] for service bookings and
// [CONFIRMED, GUESTS_SEATED, GUESTS_LEFT] for restaurant. v3 unifies both into
// one reservation source; ACTIVE ≈ seated, COMPLETED ≈ left/done.
const STATUSES: ReservationStatus[] = [
    ReservationStatus.CONFIRMED,
    ReservationStatus.PENDING,
    ReservationStatus.ACTIVE,
    ReservationStatus.COMPLETED,
];

const RESERVATION_LIMIT = {offset: 0, limit: 1000, sort: [{field: 'expectedStartDate', direction: 'ASC' as const}]};

const lsBool = (key: string, fallback: boolean): boolean =>
    localStorage.getItem(key) != null ? localStorage.getItem(key) === 'true' : fallback;

const toDateInput = (d: Date): string => {
    const p = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const ProfileAgenda = ({profileId}: Props) => {
    const {t, i18n} = useTranslation();
    const organization = useOrganization();
    const availableLanguages: string[] = organization?.supportedLanguages?.length
        ? organization.supportedLanguages
        : ['en', 'de'];

    const [start, setStart] = useState<Date>(startOfDay(new Date()));
    const [end, setEnd] = useState<Date>(endOfDay(addDays(endOfMonth(new Date()), 7)));
    const [language, setLanguage] = useState<string>(i18n.language);

    // Simple, template-free print: browser-prints exactly the agenda selection currently on screen,
    // under a hotel letterhead. Distinct from the printout system — this reflects the live
    // scope/layout/filter controls, which a fixed AGENDA_HANDOUT document cannot. Meant to be handed
    // to a guest, so the print (not the screen) is styled to look like a proper hotel document.
    // TODO: have this small print preview before the actual printout is approved and tested.
    const guestProfile = useProfiles([profileId])?.[0];
    const guestName = getProfileFullName(guestProfile);
    const orgLogo = organization?.media?.mediaURI;
    const orgAddressLine = [
        organization?.address?.addressLine1,
        [organization?.address?.postalCode, organization?.address?.cityName].filter(Boolean).join(' '),
        organization?.address?.countryName,
    ].filter(Boolean).join(', ');
    const {handlePrint, printRef} = usePrint({
        documentTitle: `Agenda${guestName ? ` - ${guestName}` : ''}`,
        // Injected only into the print document, so it both reveals the letterhead (inline
        // display:none on screen) and overrides usePrint's dense daily-list defaults (which force
        // 10px) with readable, guest-facing typography. Scoped to .agenda-print-root so nothing else
        // is affected; header selectors are two-deep to win over the default `.MuiTypography-root`.
        css: `
            .agenda-print-header { display: block !important; }
            @media print {
                .agenda-print-root { color: #111827 !important; padding: 2mm !important; }
                .agenda-print-letterhead {
                    display: flex !important;
                    justify-content: space-between !important;
                    align-items: flex-start !important;
                    gap: 16px !important;
                    border-bottom: 2px solid #111827 !important;
                    padding-bottom: 10px !important;
                    margin-bottom: 18px !important;
                }
                .agenda-print-header .agenda-print-hotel { font-size: 17px !important; font-weight: 700 !important; }
                .agenda-print-header .agenda-print-hotel-address { font-size: 11px !important; color: #666 !important; }
                .agenda-print-header .agenda-print-logo { max-height: 60px !important; max-width: 150px !important; object-fit: contain !important; }
                .agenda-print-header .agenda-print-title { font-size: 22px !important; font-weight: 700 !important; margin-bottom: 2px !important; }
                .agenda-print-header .agenda-print-guest { font-size: 15px !important; margin-bottom: 2px !important; }
                .agenda-print-header .agenda-print-range { font-size: 12px !important; color: #666 !important; margin-bottom: 22px !important; }
                .agenda-print-root .printable-page .MuiTypography-root,
                .agenda-print-root .printable-page span,
                .agenda-print-root .printable-page div { font-size: 11.5px !important; }
                .agenda-print-root .print-break { break-inside: avoid !important; page-break-inside: avoid !important; }
            }
        `,
    });

    const [scope, setScope] = useState<AgendaScope>('guest');
    const [layout, setLayout] = useState<AgendaLayout>('merged');

    const [showGuestNote, setShowGuestNote] = useState(lsBool('guest_agenda_show_guest_note', true));
    const [resourceFilter, setResourceFilter] = useState<ResourceFilter>(
        (localStorage.getItem('guest_agenda_resource_filter') as ResourceFilter) || 'all'
    );

    // --- Query A: the guest's own reservations (drives membership derivation) ---
    const guestReservationsQuery = useQuery<Page<Reservation>>(
        ['agendaReservations', profileId, start, end],
        () => ReservationService.getAllReservations({
            profileIds: [profileId],
            status: STATUSES,
            overlaps: {start, end},
            limit: RESERVATION_LIMIT,
        })
    );
    const guestReservations = useMemo(() => guestReservationsQuery.data?.content || [], [guestReservationsQuery.data]);

    // Accommodation-sector ids: membership (room/group) is derived ONLY from
    // reservations in these sectors — not spa/restaurant/activity bookings.
    const allSectors = useAllSectors();
    const accommodationSectorIds = useMemo(() => {
        const s = new Set<string>();
        allSectors.forEach(sec => sec.sectorType === SectorType.ACCOMMODATION && sec.id && s.add(sec.id));
        return s;
    }, [allSectors]);

    const guestAccommodationReservations = useMemo(
        () => filterAccommodationReservations(guestReservations, accommodationSectorIds),
        [guestReservations, accommodationSectorIds]
    );
    // Room = other guests on the guest's accommodation reservation(s). Includes the guest.
    const roomProfileIds = useMemo(() => collectProfileIds(guestAccommodationReservations), [guestAccommodationReservations]);
    const groupIds = useMemo(() => collectGroupIds(guestAccommodationReservations), [guestAccommodationReservations]);

    // --- Query B: all reservations in the guest's group(s), to derive group members ---
    const groupReservationsQuery = useQuery<Page<Reservation>>(
        ['agendaGroupReservations', groupIds, start, end],
        () => ReservationService.getAllReservations({
            reservationGroupId: groupIds,
            status: STATUSES,
            overlaps: {start, end},
            limit: RESERVATION_LIMIT,
        }),
        {enabled: groupIds.length > 0}
    );
    const groupProfileIds = useMemo(
        () => collectProfileIds(filterAccommodationReservations(groupReservationsQuery.data?.content || [], accommodationSectorIds)),
        [groupReservationsQuery.data, accommodationSectorIds]
    );

    const roomAvailable = roomProfileIds.length > 1;
    const groupAvailable = groupIds.length > 0;

    // If the selected scope stops being available (e.g. date range change), fall back to guest.
    useEffect(() => {
        if (scope === 'room' && !roomAvailable) setScope('guest');
        if (scope === 'group' && !groupAvailable) setScope('guest');
    }, [scope, roomAvailable, groupAvailable]);

    const scopeProfileIds = useMemo(() => {
        if (scope === 'room') return roomProfileIds;
        if (scope === 'group') return groupProfileIds;
        return [profileId];
    }, [scope, roomProfileIds, groupProfileIds, profileId]);

    // --- Query C: full personal agenda (all sectors) for everyone in scope ---
    const scopeReservationsQuery = useQuery<Page<Reservation>>(
        ['agendaScopeReservations', scope, scopeProfileIds, start, end],
        () => ReservationService.getAllReservations({
            profileIds: scopeProfileIds,
            status: STATUSES,
            overlaps: {start, end},
            limit: RESERVATION_LIMIT,
        }),
        {enabled: scope !== 'guest' && scopeProfileIds.length > 0}
    );

    // Displayed reservations: guest scope reuses Query A; room/group use Query C.
    const reservations = useMemo(
        () => (scope === 'guest' ? guestReservations : (scopeReservationsQuery.data?.content || [])),
        [scope, guestReservations, scopeReservationsQuery.data]
    );
    const isLoading = scope === 'guest' ? guestReservationsQuery.isLoading : scopeReservationsQuery.isLoading;

    // Resolve names for titles (services), restaurant/sector names, and resources.
    const serviceIds = new GeneralSet<string>();
    const resourceIds = new GeneralSet<string>();
    const sectorIds = new GeneralSet<string>();
    reservations.forEach(r => {
        r.serviceId && serviceIds.add(r.serviceId);
        r.resourceIds && resourceIds.addMulti(r.resourceIds);
        r.sectorId && sectorIds.add(r.sectorId);
    });
    const services = useServices(serviceIds.values());
    const resources = useResources(resourceIds.values());
    const sectors = useSectors(sectorIds.values());

    // Resource categories → derive which categories are "staff" (by name).
    const resourceCategories = useAllResourceCategories();
    const staffCategoryIds = useMemo(() => collectStaffCategoryIds(resourceCategories), [resourceCategories]);

    // Guest display names, for multi-guest labelling (room/group only).
    const scopeProfiles = useProfiles(scope === 'guest' ? [] : scopeProfileIds);
    const profileNamesById = useMemo(() => {
        const m = new Map<string, string>();
        scopeProfiles.forEach(p => p.id && m.set(p.id, getProfileFullName(p, false, '') || ''));
        return m;
    }, [scopeProfiles]);
    const scopeProfileIdsSet = useMemo(() => new Set(scopeProfileIds), [scopeProfileIds]);
    // Guests ordered by name for the split-view sections.
    const orderedScopeProfileIds = useMemo(
        () => [...scopeProfileIds].sort((a, b) => (profileNamesById.get(a) || '').localeCompare(profileNamesById.get(b) || '')),
        [scopeProfileIds, profileNamesById]
    );

    const resourcesById = useMemo(() => {
        const m = new Map<string, Resource>();
        resources.forEach(r => r.id && m.set(r.id, r));
        return m;
    }, [resources]);

    const items = useMemo(() => buildAgendaItems(reservations), [reservations]);
    const mergedDays = useMemo(() => groupAgendaItemsByDay(items), [items]);

    const dateLocale = language === 'de' ? deLocale : undefined;
    const dayHeader = (d: Date): string => formatDateFns(d, 'EEEE, dd. MMM', {locale: dateLocale});

    // Labels inside the printable area follow the selected PRINT language,
    // not the app UI language (unlike the hide-on-print toolbar, which stays `t`).
    const tp = (key: string): string => t(key, {lng: language});

    const getTitle = (r: Reservation): string => {
        const service = services.find(s => s.id === r.serviceId);
        if (service) {
            return (getLocalizedValue(language, service.nameLocalized) || service.name || '-').substring(0, 100);
        }
        // restaurant / non-service reservation: fall back to the sector name.
        return sectors.find(s => s.id === r.sectorId)?.name || '-';
    };

    const renderInfoRow = (title: string, value?: string) => {
        if (!value) return null;
        return (
            <Box sx={{display: 'flex', gap: '16px', '@media print': {gap: '2mm'}}}>
                <Typography sx={{color: '#666', width: 100, minWidth: 100}}>{title}</Typography>
                <Typography>{value}</Typography>
            </Box>
        );
    };

    const renderItem = (item: AgendaItem, index: number) => {
        const r = item.reservation;
        const range = getItemTimeRange(item);
        // Resources filtered by the selected staff / facility / all view,
        // and localized to the selected print language.
        const resourceNames = getFilteredResourceNames(r, resourcesById, staffCategoryIds, resourceFilter, language);
        const resourceValue = resourceNames.length ? resourceNames.join(', ') : undefined;
        const resourceLabel = resourceFilter === 'staff' ? tp('Staff')
            : resourceFilter === 'facility' ? tp('Facility')
                : tp('Resources');
        // In merged view a row can belong to any in-scope guest, so label who it's for.
        const guestLabel = scope !== 'guest' && layout === 'merged'
            ? getItemGuestNames(r, profileNamesById, scopeProfileIdsSet).join(', ')
            : undefined;
        // Reservation the profile booked for someone else (name stored on the request).
        const bookedForName = getBookedForName(r);
        return (
            <Box
                key={r.id}
                className={'print-break'}
                sx={{display: 'flex', marginTop: index === 0 ? 0 : '-1px', border: '1px solid #eee'}}
            >
                <Box
                    sx={{
                        width: 120, minWidth: 120,
                        borderRight: '1px solid #eee',
                        p: '8px 16px',
                        '@media print': {minWidth: '24mm', width: '24mm', p: '1mm 2mm'},
                    }}
                >
                    <Typography>
                        {formatDateHM(range.start ? new Date(range.start) : undefined)} – {formatDateHM(range.end ? new Date(range.end) : undefined)}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex', flexDirection: 'column', gap: '8px', p: '8px',
                        '@media print': {p: '1mm', gap: '1mm'},
                    }}
                >
                    <Typography variant={'body1'} fontWeight={'bold'}>
                        {getTitle(r)}
                    </Typography>

                    {guestLabel && renderInfoRow(tp('Guest'), guestLabel)}
                    {renderInfoRow(tp('Booked for'), bookedForName)}
                    {showGuestNote && renderInfoRow(tp('Guest note / remark'), getGuestNote(r))}
                    {renderInfoRow(resourceLabel, resourceValue)}
                </Box>
            </Box>
        );
    };

    const renderDays = (days: AgendaDay[], keyPrefix: string) => days.map((day, dayIndex) => (
        <div key={`${keyPrefix}-${day.day.toISOString()}`}>
            <Typography
                variant={'h6'}
                className={'print-break'}
                sx={{mt: dayIndex === 0 ? 0 : '16px', mb: '8px'}}
            >
                {dayHeader(day.day)}
            </Typography>
            {day.items.map((item, index) => renderItem(item, index))}
        </div>
    ));

    const emptyMessage = (
        <Typography className={'hide-on-print'} sx={{color: '#666'}}>
            {t('No reservations in the selected range')}
        </Typography>
    );

    const renderMerged = () => (
        <>
            {mergedDays.length === 0 && !isLoading && emptyMessage}
            {renderDays(mergedDays, 'merged')}
        </>
    );

    const renderSplit = () => {
        const guests = groupAgendaItemsByGuest(items, orderedScopeProfileIds);
        return guests.map((guest, guestIndex) => (
            <div key={guest.profileId} className={guestIndex === 0 ? undefined : 'print-break-guest'}>
                <Typography variant={'h5'} sx={{mt: guestIndex === 0 ? 0 : '24px', mb: '8px'}}>
                    {profileNamesById.get(guest.profileId) || '-'}
                </Typography>
                {guest.items.length === 0
                    ? <Typography sx={{color: '#666'}}>{tp('No entries in range')}</Typography>
                    : renderDays(groupAgendaItemsByDay(guest.items), guest.profileId)}
            </div>
        ));
    };

    const toolbar = (
        <Box
            className={'hide-on-print'}
            sx={{display: 'flex', alignItems: 'center', mb: '32px', gap: '12px', flexWrap: 'wrap'}}
        >
            {/* Simple WYSIWYG print of exactly what the selection below shows, under an
              * Agenda/guest/range header — no printout template involved. (The AGENDA_HANDOUT printout
              * page is still reachable from the daily lists / folio-style flows for the fixed
              * handout; this profile tab prints the live, filtered on-screen view instead.) */}
            <Button
                variant={'contained'}
                color={'success'}
                startIcon={<PrintIcon/>}
                onClick={() => handlePrint()}
            >
                {t('Print')}
            </Button>

            <ToggleButtonGroup
                size={'small'}
                exclusive
                value={scope}
                onChange={(e, v) => v && setScope(v)}
            >
                <ToggleButton value={'guest'}>{t('Guest')}</ToggleButton>
                <ToggleButton value={'room'} disabled={!roomAvailable}>
                    {t('Room')}{roomAvailable ? ` (${roomProfileIds.length})` : ''}
                </ToggleButton>
                <ToggleButton value={'group'} disabled={!groupAvailable}>
                    {t('Group')}{groupAvailable && groupProfileIds.length > 0 ? ` (${groupProfileIds.length})` : ''}
                </ToggleButton>
            </ToggleButtonGroup>

            {scope !== 'guest' && (
                <ToggleButtonGroup
                    size={'small'}
                    exclusive
                    value={layout}
                    onChange={(e, v) => v && setLayout(v)}
                >
                    <ToggleButton value={'merged'}>{t('Merged')}</ToggleButton>
                    <ToggleButton value={'split'}>{t('Split')}</ToggleButton>
                </ToggleButtonGroup>
            )}

            <TextField
                type={'date'}
                size={'small'}
                label={t('From')}
                InputLabelProps={{shrink: true}}
                value={toDateInput(start)}
                onChange={e => e.target.value && setStart(startOfDay(new Date(e.target.value)))}
            />
            <TextField
                type={'date'}
                size={'small'}
                label={t('To')}
                InputLabelProps={{shrink: true}}
                value={toDateInput(end)}
                onChange={e => e.target.value && setEnd(endOfDay(new Date(e.target.value)))}
            />

            <Select
                size={'small'}
                value={language}
                onChange={e => setLanguage(e.target.value)}
            >
                {availableLanguages.map(lng => (
                    <MenuItem key={lng} value={lng}>{getNameForLanguage(lng, true) || lng}</MenuItem>
                ))}
            </Select>

            <FormControlLabel
                control={<Checkbox checked={showGuestNote} onChange={(e) => {
                    setShowGuestNote(e.target.checked);
                    localStorage.setItem('guest_agenda_show_guest_note', `${e.target.checked}`);
                }}/>}
                label={t('Guest note / remark')}
            />

            <Select
                size={'small'}
                value={resourceFilter}
                onChange={e => {
                    const value = e.target.value as ResourceFilter;
                    setResourceFilter(value);
                    localStorage.setItem('guest_agenda_resource_filter', value);
                }}
            >
                <MenuItem value={'none'}>{t('No resources')}</MenuItem>
                <MenuItem value={'all'}>{t('All resources')}</MenuItem>
                <MenuItem value={'staff'}>{t('Staff')}</MenuItem>
                <MenuItem value={'facility'}>{t('Facility')}</MenuItem>
            </Select>
        </Box>
    );

    return (
        <div>
            {toolbar}

            <div ref={printRef} className={'agenda-print-root'}>
                {/* Guest-facing letterhead — print-only (inline display:none; the usePrint css above
                  * reveals and styles it in the print document, so the on-screen view is unchanged). */}
                <Box className={'agenda-print-header'} style={{display: 'none'}}>
                    <div className={'agenda-print-letterhead'}>
                        <div>
                            <div className={'agenda-print-hotel'}>{organization?.name}</div>
                            {orgAddressLine && (
                                <div className={'agenda-print-hotel-address'}>{orgAddressLine}</div>
                            )}
                        </div>
                        {orgLogo && <img className={'agenda-print-logo'} src={orgLogo} alt={''}/>}
                    </div>
                    <div className={'agenda-print-title'}>{t('Agenda')}</div>
                    {guestName && <div className={'agenda-print-guest'}>{guestName}</div>}
                    <div className={'agenda-print-range'}>
                        {formatFullDate(start)} – {formatFullDate(end)}
                    </div>
                </Box>

                <div className={'printable-page'}>
                    {layout === 'split' && scope !== 'guest' ? renderSplit() : renderMerged()}
                </div>
            </div>
        </div>
    );
};

export default ProfileAgenda;
